import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { LanguageService } from '@frontend/common';
import { catchError, map, of, throwError } from 'rxjs';
import { Tag } from '../models';

@Injectable({
  providedIn: 'root'
})
export class TagService {

  // Locale-keyed cache of translated tags: one list per UI language, held in
  // parallel and never flushed on locale change, so switching back to a
  // previously-viewed language is instant and free. Only imperative consumers read
  // this cache (via getTagsByType); those that must refresh on locale change do so
  // themselves (e.g. reloadOnLocaleChange), so the service needs no locale
  // subscription of its own.
  private _tagsByLocale = signal<Record<string, Tag[]>>({});

  /** Tags cached for the currently-active UI language (empty until fetched). */
  readonly activeTags = computed<Tag[]>(
    () => this._tagsByLocale()[this.activeLanguageKey] ?? [],
  );

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
  ) { }

  /** Tags cached for the currently-active UI language. */
  get tags(): Tag[] {
    return this.activeTags();
  }

  private get activeLanguageKey(): string {
    return this.languageService.activeLanguage().languageKey;
  }

  /**
   * Logout / personal-data contract (reserved). Tags are not personal data, so this
   * is intentionally a no-op — never repurpose it for locale handling.
   */
  clearData() {
    // no-op
  }

  private cacheTags(newTags: Tag[]): void {
    const key = this.activeLanguageKey;
    const existing = this._tagsByLocale()[key] ?? [];
    this._tagsByLocale.update((byLocale) => ({ ...byLocale, [key]: existing.concat(newTags) }));
  }

  getTagsByType (types: string[], freshFromServer : boolean){
    if (!types){return this.handleError(new HttpErrorResponse({error:'Tag type missing'}));};

    const cachedTags = this.activeTags();
    let allTypesCached = types.every(type => cachedTags.some(tag => tag.type === type));

    if(allTypesCached && !freshFromServer){
      return of(cachedTags.filter(t=> types.includes(t.type)));
    }

    const tagTypesCached = [...new Set(cachedTags.map(tag => tag.type))]; // Set gives us unique values
    const tagTypesNotCached = types.filter(type=>!tagTypesCached.includes(type));

    return this.http.get<{'data': Tag[]}>('api/v1/tags?type='+tagTypesNotCached.join(','))
      .pipe(
        map(response =>{
          if (response && response.data ){
            this.cacheTags(response.data);
            return this.activeTags().filter(t=> types.includes(t.type));
          } else {
            return [];
          }
        }),
        catchError((error) => {
          return this.handleError(error);
        })
      )
  }

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    }
    switch (errorResponse.error.message) {
      case 'The type is invalid.':
        errorMessage = 'error.data_invalid';
        break;
      default :
        // no action needed. We already set the default error message.
    }
    if (errorResponse.error.meta){
      return throwError({message:errorMessage,meta:errorResponse.error.meta});
    }
    return throwError(errorMessage);
  }


}
